import { useEffect, useRef, useState } from "react";
import { useLocale } from "../../i18n/LocaleProvider";
import type { EndReportRecord } from "../../../../shared/end-report";
import type { WorkflowSectionContext, WorkflowSectionView } from "../types";
import { conversationKey, useConversationPanel } from "../panel";
import { continueMessage, readEndReports } from "./api";
import { EndReportCard } from "./EndReportCard";
import { EndReportPanel } from "./EndReportPanel";
import { RequestPanel } from "./RequestEditor";

/**
 * The thread's end reports: a card under the transcript and the side panel.
 *
 * Reports live in the Workspace, so they are read when a thread opens and
 * again when a turn ends. A round that was not there before the turn opens
 * the panel; opening it always shows the latest round.
 *
 * Everything shown is keyed to the conversation it was read for. Switching
 * conversations shows nothing until the new one's reports arrive, so a report
 * from one thread can never be answered into another.
 */
export function useEndReportView(ctx: WorkflowSectionContext): WorkflowSectionView {
  const { t } = useLocale();
  const { busy, blocked } = ctx;
  const { token, orgId, projectId, userId, threadId } = ctx.scope;
  const key = conversationKey(ctx.scope);
  useConversationPanel(ctx);
  const [loaded, setLoaded] = useState<{ key: string; list: EndReportRecord[] }>({ key: "", list: [] });
  const [index, setIndex] = useState(0);
  // A round sent from here, until the reload after its turn shows it answered.
  const [sent, setSent] = useState<{ key: string; round: number } | null>(null);
  const known = useRef<{ key: string; latest: number }>({ key: "", latest: 0 });
  // A turn ran since the last load: only then may a new round open the panel.
  // A first turn starts with no thread, so the conversation changing is not enough.
  const ranTurn = useRef(false);
  const setPanelOpen = useRef(ctx.setPanelOpen);
  setPanelOpen.current = ctx.setPanelOpen;

  const reports = loaded.key === key ? loaded.list : [];
  const open = ctx.panelOpen;
  const sentRound = sent?.key === key ? sent.round : null;

  useEffect(() => {
    if (busy) {
      ranTurn.current = true;
      return;
    }
    const workflowId = ctx.workflowId;
    if (!ctx.enabled || !token || !orgId || !projectId || !userId || !threadId || !workflowId) {
      known.current = { key, latest: 0 };
      ranTurn.current = false;
      return;
    }
    const controller = new AbortController();
    readEndReports({ token, orgId, projectId, userId, threadId, workflowId }, controller.signal)
      .then((list) => {
        const latest = list.at(-1)?.round ?? 0;
        const before = known.current.key === key ? known.current.latest : 0;
        const afterTurn = ranTurn.current;
        known.current = { key, latest };
        ranTurn.current = false;
        setLoaded({ key, list });
        setSent(null);
        if (afterTurn && latest > before) {
          setIndex(list.length - 1);
          setPanelOpen.current(true);
        }
      })
      .catch(() => {
        // Nothing to show for this conversation; it still works without reports.
        if (!controller.signal.aborted) setLoaded({ key, list: [] });
      });
    return () => controller.abort();
  }, [ctx.enabled, token, key, busy, orgId, projectId, userId, threadId]);

  // However it opens (here or from the rail), the panel opens on the latest round.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (ctx.panelOpen && !wasOpen.current) setIndex(reports.length - 1);
    wasOpen.current = ctx.panelOpen;
  }, [ctx.panelOpen, reports.length]);

  const latest = reports.at(-1) ?? null;
  const show = () => {
    setIndex(reports.length - 1);
    ctx.setPanelOpen(true);
  };

  function submit(record: EndReportRecord | null, selected: string[], note: string) {
    // Only a report read for this very conversation can be answered into it.
    if (busy || blocked || !threadId || (record && loaded.key !== key)) return;
    const answerable = record !== null && record === latest && !record.submission && sentRound !== record.round;
    const labels = answerable ? record.report.work.nextSteps.filter((step) => selected.includes(step.id)).map((step) => step.label) : [];
    if (!labels.length && !note.trim()) return;
    ctx.send(continueMessage(labels, note, t), {
      ...(answerable ? { body: { end_report_submission: { round: record.round, selected, note } } } : {}),
      // A newer action or edit may arrive while the request is being accepted.
      onAccepted: () => ctx.setDraft((current) => current === note ? "" : current),
    });
    if (answerable) setSent({ key, round: record.round });
    ctx.setPanelOpen(false);
  }

  const shown = reports[Math.min(index, reports.length - 1)];
  return {
    rail: reports.length || ctx.draft.trim() ? {
      label: t("endReport.title"),
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="8" y="2" width="8" height="4" rx="1" />
          <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
          <path d="m9 14 2 2 4-4" />
        </svg>
      ),
    } : null,
    card: latest && !busy ? (
      <div className="chat-msg assistant end-report-row">
        <EndReportCard record={latest} sent={!!latest.submission || sentRound === latest.round} onOpen={show} />
      </div>
    ) : null,
    panel: open && shown ? (
      <EndReportPanel
        key={`${key}:${shown.round}`}
        record={shown}
        position={{ index: reports.indexOf(shown), total: reports.length }}
        sent={sentRound === shown.round}
        latest={shown === latest}
        disabled={busy || blocked}
        note={ctx.draft}
        onNoteChange={ctx.setDraft}
        requestFocus={ctx.requestFocus}
        onPage={(next) => setIndex(Math.max(0, Math.min(reports.length - 1, next)))}
        onSubmit={(selected, note) => submit(shown, selected, note)}
      />
    ) : open ? <RequestPanel
      note={ctx.draft} onNoteChange={ctx.setDraft} requestFocus={ctx.requestFocus}
      disabled={busy || blocked} onSubmit={(note) => submit(null, [], note)}
    /> : null,
  };
}
